/**
 * SBDFart - Silent But Deadly, nearly invisible fast projectile
 * 
 * Behavior:
 * 1. Fires in the direction player is facing
 * 2. Nearly invisible (5% opacity) with subtle heat shimmer
 * 3. Travels fast (150% player speed) in straight line
 * 4. High damage (35) with long stun (1.5s)
 * 5. Destroys on hitting a wall or player
 * 6. Cannot damage owner
 */

import Phaser from 'phaser';
import { FartType, FART_CONFIGS, SBD_CONFIG } from './FartTypes';
import { IFart } from '../../interfaces/GameInterfaces';
import { TileType } from '../../systems/MazeGenerator';
import { TILE_SIZE } from '../../config/game.config';

export interface SBDFartConfig {
    ownerId: string;
    direction: { x: number; y: number }; // Normalized direction vector
    mazeData: TileType[][];
}

export class SBDFart extends Phaser.Physics.Arcade.Sprite implements IFart {
    public readonly fartType: FartType = FartType.SBD;

    private ownerId: string;
    private direction: { x: number; y: number };
    private mazeData: TileType[][];
    private hasHit: boolean = false;

    // Config values
    private readonly SPEED = SBD_CONFIG.speed;
    private readonly BASE_OPACITY = SBD_CONFIG.opacity;
    private readonly SHIMMER_FREQ = SBD_CONFIG.shimmerFrequency;
    private readonly DURATION = FART_CONFIGS[FartType.SBD].duration;
    private readonly DAMAGE = FART_CONFIGS[FartType.SBD].damage;
    private readonly STUN_DURATION = FART_CONFIGS[FartType.SBD].stunDuration;

    constructor(scene: Phaser.Scene, x: number, y: number, config: SBDFartConfig) {
        super(scene, x, y, 'fart_sbd');

        this.ownerId = config.ownerId;
        this.direction = config.direction;
        this.mazeData = config.mazeData;

        // Add to scene and physics
        scene.add.existing(this);
        scene.physics.add.existing(this);

        // Configure physics
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.setCircle(8);
        body.setOffset(4, 4);
        body.setCollideWorldBounds(true);
        body.onWorldBounds = true;

        // Set velocity based on direction
        body.setVelocity(
            this.direction.x * this.SPEED,
            this.direction.y * this.SPEED
        );

        // Visual setup - nearly invisible
        this.setDepth(42);
        this.setScale(0.8);
        this.setAlpha(this.BASE_OPACITY);

        // Store owner data
        this.setData('ownerId', this.ownerId);
        this.setData('isSBD', true);

        // Start shimmer effect
        this.startShimmerEffect();

        // Set lifetime timer
        scene.time.delayedCall(this.DURATION, () => {
            if (this.active) this.fadeOut();
        });

        // Handle world bounds collision
        scene.physics.world.on('worldbounds', (body: Phaser.Physics.Arcade.Body) => {
            if (body.gameObject === this) {
                this.hitWall();
            }
        });
    }

    /**
     * Create subtle heat shimmer effect
     */
    private startShimmerEffect(): void {
        this.scene.tweens.add({
            targets: this,
            alpha: { from: this.BASE_OPACITY, to: this.BASE_OPACITY * 2 },
            duration: this.SHIMMER_FREQ,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });

        // Subtle scale pulse
        this.scene.tweens.add({
            targets: this,
            scaleX: { from: 0.7, to: 0.9 },
            scaleY: { from: 0.7, to: 0.9 },
            duration: this.SHIMMER_FREQ * 1.5,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }

    /**
     * Update - check for wall collisions
     */
    update(time: number, delta: number): void {
        if (!this.active || this.hasHit) return;

        // Check tile at current position
        const tileX = Math.floor(this.x / TILE_SIZE);
        const tileY = Math.floor(this.y / TILE_SIZE);

        if (tileY >= 0 && tileY < this.mazeData.length &&
            tileX >= 0 && tileX < this.mazeData[0].length) {
            if (this.mazeData[tileY][tileX] === TileType.WALL) {
                this.hitWall();
            }
        }
    }

    /**
     * Handle hitting a wall
     */
    private hitWall(): void {
        if (this.hasHit) return;
        this.hasHit = true;

        // Stop movement
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.setVelocity(0, 0);

        // Small puff effect
        this.scene.tweens.add({
            targets: this,
            alpha: 0.3,
            scaleX: 1.5,
            scaleY: 1.5,
            duration: 100,
            onComplete: () => this.destroy()
        });
    }

    /**
     * Handle hitting a player
     */
    hitPlayer(): void {
        if (this.hasHit) return;
        this.hasHit = true;

        // Stop movement
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.setVelocity(0, 0);

        // Impact effect
        this.scene.tweens.add({
            targets: this,
            alpha: 0.5,
            scaleX: 2,
            scaleY: 2,
            duration: 150,
            onComplete: () => this.destroy()
        });
    }

    /**
     * Fade out when duration expires
     */
    private fadeOut(): void {
        if (this.hasHit) return;

        this.scene.tweens.add({
            targets: this,
            alpha: 0,
            duration: 200,
            onComplete: () => this.destroy()
        });
    }

    /**
     * Check if this SBD can damage a player
     */
    canDamagePlayer(playerId: string): boolean {
        // Cannot damage owner
        if (playerId === this.ownerId) return false;

        // Already hit something
        if (this.hasHit) return false;

        return true;
    }

    /**
     * Get the owner ID
     */
    getOwnerId(): string {
        return this.ownerId;
    }

    /**
     * IFart interface - get damage value
     */
    getDamage(): number {
        return this.DAMAGE;
    }

    /**
     * IFart interface - get stun duration
     */
    getStunDuration(): number {
        return this.STUN_DURATION;
    }

    /**
     * Override destroy to clean up
     */
    destroy(fromScene?: boolean): void {
        super.destroy(fromScene);
    }
}
